import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { REPORT_ISSUE_EVENT } from './HelpWidget';

const openReportIssue = () => {
  window.dispatchEvent(new Event(REPORT_ISSUE_EVENT));
};

const ADMIN_EMAIL = 'tambe.raju18@gmail.com';

// `pro: true` items are gated by ProGate at the route level; we show a "PRO"
// badge in the sidebar so Free users see what's locked before clicking through.
const NAV = [
  { to: '/dashboard',  icon: '📊', label: 'Dashboard' },
  { to: '/deals',      icon: '🤝', label: 'Brand Deals' },
  { to: '/invoices',   icon: '🧾', label: 'GST Invoices', pro: true },
  { to: '/mediakit',   icon: '📁', label: 'Media Kit',    pro: true },
  { to: '/ratecard',   icon: '💰', label: 'Rate Card',    pro: true },
  { to: '/payments',   icon: '💸', label: 'TDS Tracker',  pro: true },
  { to: '/analytics',  icon: '📈', label: 'Analytics' },
  { to: '/contracts',  icon: '📄', label: 'Contracts',    pro: true },
  { to: '/repurpose',  icon: '🔄', label: 'Repurpose',    pro: true },
  { to: '/outreach',   icon: '📧', label: 'Outreach',     pro: true },
  { to: '/trending',   icon: '🔥', label: 'Trending' },
  { to: '/collabs',    icon: '🤝', label: 'Collabs',      pro: true },
  { to: '/bio',        icon: '🔗', label: 'Link-in-Bio' },
  { to: '/captions',   icon: '✨', label: 'Captions' },
  { to: '/calendar',   icon: '📅', label: 'Calendar' },
  { to: '/ideas',      icon: '💡', label: 'Ideas' },
  { to: '/account',    icon: '⚙️', label: 'Account' },
  { to: '/help',       icon: '❓', label: 'Help' },
];

/* Bottom tabs — 5 most used for quick access */
const BOTTOM_TABS = [
  { to: '/dashboard', icon: '📊', label: 'Home'     },
  { to: '/deals',     icon: '🤝', label: 'Deals'    },
  { to: '/invoices',  icon: '🧾', label: 'Invoices' },
  { to: '/payments',  icon: '💸', label: 'TDS'      },
  { to: '/bio',       icon: '🔗', label: 'Bio'      },
];

export default function Sidebar() {
  const { user, logout }  = useAuth();
  const navigate          = useNavigate();
  const location          = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* Close drawer on route change */
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  /* Prevent body scroll when drawer open */
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const handleLogout = () => { logout(); navigate('/'); };

  const navLinkStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    padding: '0.65rem 0.9rem', borderRadius: '10px',
    textDecoration: 'none', fontSize: '0.875rem', fontWeight: 600,
    transition: 'all 0.15s',
    background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
    color: isActive ? 'white' : 'rgba(255,255,255,0.55)',
  });

  /* ── DESKTOP SIDEBAR ─────────────────────────────── */
  const DesktopSidebar = (
    <div className="sidebar">
      {/* Logo */}
      <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4dba7a', display: 'inline-block' }} />
          CreatorKhata
        </div>
        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', marginTop: '0.2rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Creator Dashboard
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '1rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '0.2rem', overflowY: 'auto' }}>
        {NAV.map(n => (
          <NavLink key={n.to} to={n.to} style={({ isActive }) => navLinkStyle(isActive)}>
            <span style={{ fontSize: '1rem' }}>{n.icon}</span>
            <span style={{ flex: 1 }}>{n.label}</span>
            {n.pro && user?.effectivePlan !== 'PRO' && user?.plan !== 'PRO' && (
              <span style={{
                fontSize: '0.55rem', fontWeight: 800, letterSpacing: '0.04em',
                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                color: 'white', padding: '0.1rem 0.4rem', borderRadius: 4
              }}>PRO</span>
            )}
          </NavLink>
        ))}

        {/* Report an issue — opens modal via window event */}
        <div style={{ margin: '0.5rem 0', borderTop: '1px solid rgba(255,255,255,0.08)' }} />
        <button
          type="button"
          onClick={openReportIssue}
          style={{
            ...navLinkStyle(false),
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontFamily: 'inherit',
            width: '100%',
            textAlign: 'left',
          }}
          title="Report a bug, send feedback, or request a feature"
        >
          <span style={{ fontSize: '1rem' }}>📝</span>
          <span style={{ flex: 1 }}>Report Issue</span>
        </button>

        {/* Admin-only: Issues */}
        {user?.email === ADMIN_EMAIL && (
          <NavLink to="/issues" style={({ isActive }) => navLinkStyle(isActive)}>
            <span style={{ fontSize: '1rem' }}>🎫</span>
            Issues
            <span style={{ marginLeft: 'auto', fontSize: '0.6rem', fontWeight: 700, background: '#ef4444', color: 'white', borderRadius: '4px', padding: '0.1rem 0.35rem' }}>
              ADMIN
            </span>
          </NavLink>
        )}
      </nav>

      {/* User */}
      <div style={{ padding: '1rem 0.75rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.75rem' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#1a7a4a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
            {user?.name?.[0]?.toUpperCase() || 'C'}
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name || 'Creator'}</div>
            <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email || ''}</div>
          </div>
        </div>
        <button onClick={handleLogout}
          style={{ width: '100%', padding: '0.5rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
          Sign Out
        </button>
      </div>
    </div>
  );

  /* ── MOBILE TOP BAR ──────────────────────────────── */
  const MobileTopBar = (
    <div className="mobile-topbar">
      {/* Hamburger */}
      <button onClick={() => setDrawerOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.4rem', borderRadius: '8px', fontSize: '1.3rem', lineHeight: 1 }}>
        ☰
      </button>

      {/* Logo */}
      <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1.1rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4dba7a', display: 'inline-block' }} />
        CreatorKhata
      </div>

      {/* Avatar */}
      <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#1a7a4a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, color: 'white' }}>
        {user?.name?.[0]?.toUpperCase() || 'C'}
      </div>
    </div>
  );

  /* ── MOBILE DRAWER ───────────────────────────────── */
  const MobileDrawer = (
    <>
      {/* Backdrop */}
      {drawerOpen && (
        <div onClick={() => setDrawerOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, backdropFilter: 'blur(2px)' }} />
      )}

      {/* Slide-in panel */}
      <div style={{
        position: 'fixed', top: 0, left: 0, bottom: 0, width: '280px',
        background: '#0f1117', zIndex: 201, display: 'flex', flexDirection: 'column',
        transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: '4px 0 24px rgba(0,0,0,0.3)',
      }}>
        {/* Drawer header */}
        <div style={{ padding: '1.25rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1.15rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4dba7a', display: 'inline-block' }} />
            CreatorKhata
          </div>
          <button onClick={() => setDrawerOpen(false)}
            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '1rem', cursor: 'pointer', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ✕
          </button>
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '1rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '0.15rem', overflowY: 'auto' }}>
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to} style={({ isActive }) => navLinkStyle(isActive)}>
              <span style={{ fontSize: '1rem' }}>{n.icon}</span>
              {n.label}
            </NavLink>
          ))}

          {/* Report an issue — opens modal via window event */}
          <div style={{ margin: '0.5rem 0', borderTop: '1px solid rgba(255,255,255,0.08)' }} />
          <button
            type="button"
            onClick={() => { openReportIssue(); setDrawerOpen(false); }}
            style={{
              ...navLinkStyle(false),
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: '1rem' }}>📝</span>
            <span style={{ flex: 1 }}>Report Issue</span>
          </button>

          {/* Admin-only: Issues */}
          {user?.email === ADMIN_EMAIL && (
            <NavLink to="/issues" style={({ isActive }) => navLinkStyle(isActive)}>
              <span style={{ fontSize: '1rem' }}>🎫</span>
              Issues
              <span style={{ marginLeft: 'auto', fontSize: '0.6rem', fontWeight: 700, background: '#ef4444', color: 'white', borderRadius: '4px', padding: '0.1rem 0.35rem' }}>
                ADMIN
              </span>
            </NavLink>
          )}
        </nav>

        {/* User + logout */}
        <div style={{ padding: '1rem 0.75rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.75rem' }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#1a7a4a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
              {user?.name?.[0]?.toUpperCase() || 'C'}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name || 'Creator'}</div>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email || ''}</div>
            </div>
          </div>
          <button onClick={handleLogout}
            style={{ width: '100%', padding: '0.55rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
            Sign Out
          </button>
        </div>
      </div>
    </>
  );

  /* ── MOBILE BOTTOM TAB BAR ───────────────────────── */
  const BottomTabBar = (
    <div className="mobile-bottom-nav">
      {BOTTOM_TABS.map(t => {
        const isActive = location.pathname === t.to;
        return (
          <NavLink key={t.to} to={t.to}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', textDecoration: 'none', padding: '0.5rem 0', borderRadius: '12px', transition: 'all 0.15s', color: isActive ? '#4dba7a' : 'rgba(255,255,255,0.45)' }}>
            <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{t.icon}</span>
            <span style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.02em' }}>{t.label}</span>
            {isActive && <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#4dba7a', marginTop: '1px' }} />}
          </NavLink>
        );
      })}
      {/* More button */}
      <button onClick={() => setDrawerOpen(true)}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem 0', color: 'rgba(255,255,255,0.45)' }}>
        <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>☰</span>
        <span style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.02em' }}>More</span>
      </button>
    </div>
  );

  return (
    <>
      {DesktopSidebar}
      {MobileTopBar}
      {MobileDrawer}
      {BottomTabBar}
    </>
  );
}
